import { t } from 'i18next';

import { Client } from 'src/constants';
import { RootState } from 'src/store';
import {
  addCompanyToClients,
  removeCompanyFromClients,
} from 'src/store/clients/actions';
import {
  ADD_CLIENT_SUCCESS,
  ADD_COMPANY_SUCCESS,
  AddressCustomFieldValues,
  Company,
  DELETE_COMPANIES_SUCCESS,
  REPLACE_OPTIMISTIC_COMPANY,
  UPDATE_CLIENTS_SUCCESS,
  UPDATE_COMPANIES_SUCCESS,
  UPDATE_COMPANY_SUCCESS,
} from 'src/store/clients/types';
import { addUpdate } from 'src/store/optimisticUpdates/slice';
import { alertSnackbar } from 'src/store/ui/actions';
import { MODIFY_INTERNAL_USERS } from 'src/store/users/types';
import { ApiError } from 'src/utils/Errors';

import { ApiMethods, ApiTags, appAPI } from '.';

type getCompaniesInput = {
  ignoreAccess?: boolean;
};

type ClientInput = {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};
type CompanyInput = {
  name: string;
  clients: Array<ClientInput>;
  promotePlaceholders?: boolean;
};

type PatchCompanyInput = {
  companyId: string;
  name?: string;
  customFields?: Record<string, string | string[] | AddressCustomFieldValues>;
};

export type UnprocessedClientResponse = {
  client: ClientInput;
  error: string;
};

export const PLACEHOLDER_PROMOTION_ERROR =
  'placeholder company has blocking resources';

type ManageCompanyClientError = {
  clientId: string;
  error: ApiError;
};

type CreateCompanyResponse = {
  company: Company;
  createdClients: Client[];
  updatedClients: Client[];
  unprocessedClients: Array<UnprocessedClientResponse>;
};

export const companiesApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createCompany: build.mutation<CreateCompanyResponse, CompanyInput>({
      query: (newCompany) => ({
        path: `/v1/companies`,
        method: ApiMethods.post,
        options: {
          body: newCompany,
        },
      }),
      invalidatesTags: [
        ApiTags.companies,
        ApiTags.clientCompanies,
        ApiTags.clientUsers,
      ],
      async onQueryStarted(
        companyInput,
        { dispatch, queryFulfilled, getState },
      ) {
        const state = getState() as RootState;
        const currentUserId = state.user.id;
        const { internalUsers } = state.users;
        const { companies } = state.clients;

        // Create optimistic company for immediate UI update
        const optimisticCompany: Company = {
          id: `temp-${Date.now()}`, // Temporary ID
          fields: {
            name: companyInput.name,
            isPlaceholder: false,
            fallbackColor: '#938EAB', // Default fallback color
            avatarImageURL: '',
            primaryDomain: '',
            associatedDomains: [],
            customerId: '',
          },
          ownerId: currentUserId || '',
          identityId: `temp-identity-${Date.now()}`,
          owner: null,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
          entityStatus: 'ACTIVE',
          getstreamId: `temp-getstream-${Date.now()}`,
          metadata: {},
        };

        // Optimistically add the company to the store

        dispatch({ type: ADD_COMPANY_SUCCESS, payload: [optimisticCompany] });
        // Optimistically update internal user's company access
        dispatch({
          type: MODIFY_INTERNAL_USERS,
          payload: internalUsers.map((user) => {
            if (user.id === currentUserId) {
              return {
                ...user,
                companyAccessList: [
                  ...(user.companyAccessList ?? []),
                  optimisticCompany.id,
                ],
              };
            }
            return user;
          }),
        });

        // Optimistically update client's company associations for existing clients
        const existingClientIds = companyInput.clients
          .filter((client) => client.id)
          .map((client) => client.id!);

        if (existingClientIds.length > 0) {
          dispatch(
            addCompanyToClients(existingClientIds, optimisticCompany.id),
          );
        }

        try {
          const { data } = await queryFulfilled;
          const isCompanyUpdateIncomplete =
            !data.company && data.unprocessedClients.length > 0;

          // make sure we the created company we have is not already in the store
          // if it is then we should drop the optimistic company and the other
          // places that its added to.
          const isExistingCompanyUpdated =
            data.company && companies.find((c) => c.id === data.company.id);
          if (isExistingCompanyUpdated || isCompanyUpdateIncomplete) {
            // Remove the optimistic company from the store
            dispatch({
              type: DELETE_COMPANIES_SUCCESS,
              payload: [optimisticCompany.id],
              removeFromAllCompanies: true,
            });

            // Revert optimistic updates on placeholder promotion failure
            if (existingClientIds.length > 0) {
              dispatch(
                removeCompanyFromClients(
                  existingClientIds,
                  optimisticCompany.id,
                ),
              );
            }

            // Revert internal user's company access
            dispatch({
              type: MODIFY_INTERNAL_USERS,
              payload: internalUsers.map((user) => {
                if (user.id === currentUserId) {
                  return {
                    ...user,
                    companyAccessList: (user.companyAccessList ?? []).filter(
                      (c) => c !== optimisticCompany.id,
                    ),
                  };
                }
                return user;
              }),
            });

            if (isExistingCompanyUpdated) {
              // update the store so that the changes made to the existing
              // company are reflected in the UI. Notably, the placeholder
              // company should now show as a named company.
              dispatch({
                type: UPDATE_COMPANIES_SUCCESS,
                payload: [data.company],
              });
            }

            return;
          }

          // Replace optimistic company with real company in a single atomic action
          dispatch({
            type: REPLACE_OPTIMISTIC_COMPANY,
            payload: {
              optimisticCompanyId: optimisticCompany.id,
              realCompany: data.company,
            },
          });

          // Update client associations with real company ID
          if (existingClientIds.length > 0) {
            // Remove the optimistic association
            dispatch(
              removeCompanyFromClients(existingClientIds, optimisticCompany.id),
            );
            // Add the real association
            dispatch(addCompanyToClients(existingClientIds, data.company.id));
          }

          // Handle created and updated clients
          if (data.createdClients.length) {
            dispatch({
              type: ADD_CLIENT_SUCCESS,
              payload: data.createdClients,
            });
          }
          if (data.updatedClients.length) {
            dispatch({
              type: UPDATE_CLIENTS_SUCCESS,
              payload: data.updatedClients,
            });
          }

          // Update internal users company access
          dispatch({
            type: MODIFY_INTERNAL_USERS,
            payload: internalUsers.map((user) => {
              if (user.id === currentUserId) {
                return {
                  ...user,
                  companyAccessList: [
                    ...(user.companyAccessList ?? []).filter(
                      (c) => c !== optimisticCompany.id,
                    ),
                    data.company.id,
                  ],
                };
              }
              return user;
            }),
          });
        } catch (e) {
          // Show error toast
          dispatch(
            alertSnackbar({
              errorMessage: `Failed to create ${companyInput.name}, please try again.`,
            }),
          );
          // Revert optimistic updates on error
          if (existingClientIds.length > 0) {
            dispatch(
              removeCompanyFromClients(existingClientIds, optimisticCompany.id),
            );
          }
          // Remove the optimistic company from the store
          dispatch({
            type: DELETE_COMPANIES_SUCCESS,
            payload: [optimisticCompany.id],
          });
          // Revert internal user's company access
          dispatch({
            type: MODIFY_INTERNAL_USERS,
            payload: internalUsers.map((user) => {
              if (user.id === currentUserId) {
                return {
                  ...user,
                  companyAccessList: (user.companyAccessList ?? []).filter(
                    (c) => c !== optimisticCompany.id,
                  ),
                };
              }
              return user;
            }),
          });
        }
      },
    }),
    patchCompany: build.mutation<Company, PatchCompanyInput>({
      query: ({ companyId, name, customFields }) => ({
        path: `/v1/companies/${companyId}`,
        method: ApiMethods.patch,
        options: {
          body: {
            name,
            customFields,
          },
        },
      }),
      async onQueryStarted(
        { companyId, name, customFields },
        { dispatch, queryFulfilled, getState },
      ) {
        // Get current state to find the company being updated
        const state = getState() as RootState;
        const currentCompany = state.clients.companies.find(
          (c) => c.id === companyId,
        );

        if (currentCompany) {
          // Create optimistic update
          const optimisticUpdate = {
            ...currentCompany,
            fields: {
              ...currentCompany.fields,
              ...(name && { name }),
              // Merge the old custom fields with the new ones so we don’t lose any existing
              // values. This keeps all previously saved fields in place while only updating
              // the ones the user changed.
              ...(customFields && {
                customFields: {
                  ...(currentCompany?.fields.customFields ?? {}),
                  ...customFields,
                },
              }),
            },
            updatedDate: new Date().toISOString(),
          };

          // Optimistically update the RTK Query cache
          dispatch(
            companiesApi.util.updateQueryData(
              'getCompanies',
              undefined,
              (draft) => {
                const index = draft.findIndex((c) => c.id === companyId);
                if (index !== -1) {
                  draft[index] = optimisticUpdate;
                }
              },
            ),
          );

          // Optimistically update the Redux state
          dispatch({
            type: UPDATE_COMPANY_SUCCESS,
            payload: {
              companyId,
              companyName: name,
              customFields: {
                ...(currentCompany?.fields.customFields ?? {}),
                ...customFields,
              },
            },
          });
        }

        try {
          const { data } = await queryFulfilled;

          // Update the cache with the actual response
          dispatch(
            companiesApi.util.updateQueryData(
              'getCompanies',
              undefined,
              (draft) => {
                const index = draft.findIndex((c) => c.id === companyId);
                if (index !== -1) {
                  draft[index] = data;
                }
              },
            ),
          );
        } catch (error) {
          // Revert optimistic update on error
          if (currentCompany) {
            // Revert RTK Query cache
            dispatch(
              companiesApi.util.updateQueryData(
                'getCompanies',
                undefined,
                (draft) => {
                  const index = draft.findIndex((c) => c.id === companyId);
                  if (index !== -1) {
                    draft[index] = currentCompany;
                  }
                },
              ),
            );

            // Revert Redux state
            dispatch({
              type: UPDATE_COMPANY_SUCCESS,
              payload: {
                companyId,
                companyName: currentCompany.fields.name,
                customFields: currentCompany.fields.customFields,
              },
            });
          }

          // Show error toast
          dispatch(
            alertSnackbar({
              errorMessage: `Failed to update company. Please try again.`,
            }),
          );
        }
      },
    }),
    getCompanies: build.query<Company[], getCompaniesInput | undefined>({
      query: (getCompaniesInput) => ({
        path: `/v1/companies`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: getCompaniesInput,
        },
      }),
      providesTags: [ApiTags.clientCompanies],
    }),
    manageCompanyClients: build.mutation<
      {
        addedClientIds: string[];
        addClientErrors: ManageCompanyClientError[];
        removedClientIds: string[];
        removeClientErrors: ManageCompanyClientError[];
        promotePlaceholders?: boolean;
        deletePlaceholder?: boolean;
      },
      {
        companyId: string;
        addClientIds: string[];
        removeClientIds: string[];
        promotePlaceholders?: boolean;
        deletePlaceholder?: boolean;
      }
    >({
      query: ({
        companyId,
        addClientIds,
        removeClientIds,
        promotePlaceholders,
        deletePlaceholder,
      }) => ({
        path: `/v1/companies/${companyId}/clients`,
        method: ApiMethods.post,
        options: {
          body: {
            addClientIds,
            removeClientIds,
            promotePlaceholders: promotePlaceholders ?? false,
            deletePlaceholder: deletePlaceholder ?? false,
          },
        },
      }),
      async onQueryStarted(
        { addClientIds, removeClientIds, companyId },
        { dispatch, queryFulfilled },
      ) {
        if (addClientIds.length > 0) {
          dispatch(
            addUpdate({
              entityType: 'CLIENT_USER',
              itemId: addClientIds?.at(0) ?? '',
            }),
          );
          dispatch(addCompanyToClients(addClientIds, companyId));
        }
        if (removeClientIds.length > 0) {
          dispatch(
            addUpdate({
              entityType: 'CLIENT_USER',
              itemId: removeClientIds?.at(0) ?? '',
            }),
          );
          dispatch(removeCompanyFromClients(removeClientIds, companyId));
        }

        const updateClient = (
          clientIds: string[],
          action: 'add' | 'remove',
        ) => {
          if (action === 'add') {
            dispatch(addCompanyToClients(clientIds, companyId));
          } else {
            dispatch(removeCompanyFromClients(clientIds, companyId));
          }
        };

        const labels = {
          client: t(($) => $.client, { count: 1 }).toLowerCase(),
          company: t(($) => $.company, { count: 1 }).toLowerCase(),
          companies: t(($) => $.company, { count: 2 }).toLowerCase(),
        };

        // update the client state with response payload.
        // the response contains the ids of the added/removed clients.
        // if the client update is successful, we're good since the state is updated optimistically.
        // if the client update is not successful, we revert the optimistically updated client state. Unfortunately,
        // we cannot leverage the rtk patch undo function as the clients state is not stored in the rtk query cache.
        try {
          const response = await queryFulfilled;

          if (response.data.addClientErrors.length > 0) {
            // Group errors by type for better user messaging
            const limitErrors = response.data.addClientErrors.filter((error) =>
              error.error.message?.includes('too many companies'),
            );
            const conflictErrors = response.data.addClientErrors.filter(
              (error) =>
                error.error.type === 'record_conflict' ||
                error.error.type === 'require_confirmation',
            );

            // Show appropriate toast message based on error type
            if (limitErrors.length > 0) {
              dispatch(
                alertSnackbar({
                  errorMessage: `Cannot add ${labels.client} to ${labels.company}: ${labels.client} has reached the maximum number of ${labels.companies} allowed by your plan.`,
                }),
              );
            } else if (
              conflictErrors.length === 0 &&
              response.data.addClientErrors.length > 0
            ) {
              // Generic error for other failures
              dispatch(
                alertSnackbar({
                  errorMessage: `Failed to add ${labels.client} to ${labels.company}. Please try again.`,
                }),
              );
            }

            // remove the errored clients from the state
            updateClient(
              response.data.addClientErrors.map((error) => error.clientId),
              'remove',
            );
          }

          if (response.data.removeClientErrors.length > 0) {
            // Show error toast for remove failures
            dispatch(
              alertSnackbar({
                errorMessage: `Failed to remove ${labels.client} from ${labels.company}. Please try again.`,
              }),
            );

            // add back the clients that were removed but errored
            updateClient(
              response.data.removeClientErrors.map((error) => error.clientId),
              'add',
            );
          }
        } catch (e) {
          // Show error toast for network/server failures
          dispatch(
            alertSnackbar({
              errorMessage: `Failed to update ${labels.company} associations. Please check your connection and try again.`,
            }),
          );

          // revert the optimistically updated client state.
          if (addClientIds.length > 0) {
            dispatch(removeCompanyFromClients(addClientIds, companyId));
          }
          if (removeClientIds.length > 0) {
            dispatch(addCompanyToClients(removeClientIds, companyId));
          }
        }
      },
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  usePatchCompanyMutation,
  useManageCompanyClientsMutation,
} = companiesApi;
